import { motion } from "framer-motion";
import { TrendingUp, ShieldCheck, HeartHandshake } from "lucide-react";

const stats = [
  { value: "60%", label: "of UK employees have experienced a mental health issue due to work — or where work was a contributing factor." },
  { value: "31%", label: "of UK employees have been formally diagnosed with a mental health condition." },
  { value: "800k+", label: "adults in the UK are now certified Mental Health First Aiders." },
];

const advantages = [
  { icon: ShieldCheck, title: "Evidence based", body: "Rigorously tested, research-grounded curriculum used by thousands of UK organisations." },
  { icon: TrendingUp, title: "Royal Society for Public Health accredited", body: "Instructor training accredited by the RSPH — the standard for public health training." },
  { icon: HeartHandshake, title: "Blended learning", body: "Discussions, case studies, lived-experience film clips and experiential activities — not just slides." },
];

export default function WhyMHFA() {
  return (
    <section data-testid="why-mhfa-section" className="section bg-[#F3EFEA] relative">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <div className="md:col-span-5">
            <p className="eyebrow mb-4">Why it matters</p>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#1E2A25]">
              Mental health is a <span className="italic text-[#4A5D52]">workplace</span> issue.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-lg text-[#4A5D52] leading-relaxed">
              The cost of not acting is real — for people, for productivity and for culture.
              MHFA gives organisations a proven framework to change that.
            </p>
          </div>
        </div>

        {/* Stats broken grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-24">
          {stats.map((s, i) => {
            const spans = ["md:col-span-5 md:col-start-1", "md:col-span-4 md:col-start-7 md:mt-16", "md:col-span-4 md:col-start-2 md:mt-4"];
            return (
              <motion.div
                key={i}
                data-testid={`stat-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.12 }}
                className={`${spans[i]} bg-[#FDFBF7] rounded-3xl p-10 border border-[#8DA399]/25`}
              >
                <div className="font-serif-display text-6xl md:text-7xl text-[#4A5D52] leading-none">{s.value}</div>
                <p className="mt-6 text-[#1E2A25]/80 leading-relaxed">{s.label}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Advantages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {advantages.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={i}
                data-testid={`advantage-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="pt-6 border-t border-[#4A5D52]/25"
              >
                <Icon className="w-6 h-6 text-[#D88C76]" strokeWidth={1.5} />
                <h3 className="font-serif-display text-2xl mt-4 text-[#1E2A25]">{a.title}</h3>
                <p className="mt-3 text-[#4A5D52] leading-relaxed">{a.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
